import type { FC } from "react"
import { useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"

import type { CreatePositionStore } from "@/domains/thick-v1/stores/create-position-store"
import type { PoolOhlcStore } from "@/domains/thick-v1/stores/pool-ohlc-store"

import { PriceHistoryWithRangeView } from "./price-history-with-range-view"

/**
 * Thin composite that binds the already-singleton {@link PoolOhlcStore} and
 * {@link CreatePositionStore} to {@link PriceHistoryWithRangeView}. Its only
 * side-effect is forwarding the selected pool's address into `PoolOhlcStore`
 * so the OHLC query kicks off when the create page mounts with a pool —
 * mirroring the lifecycle wiring used by {@link PoolDetailPageComposite}.
 *
 * The composite owns MobX coupling and the `useEffect` route/store fan-out
 * required by architecture rules; the view it renders is pure and
 * Storybook-testable.
 */
export interface PriceHistoryCompositeProps {
  createPositionStore: CreatePositionStore
  poolOhlcStore: PoolOhlcStore
  /** Optional override for visual density; view defaults to 120 px tall. */
  height?: number
  className?: string
}

/**
 * Zoom sits out of the store because it's transient UI state scoped to a
 * single chart render, and encoding it in `PoolOhlcStore` would pollute a
 * singleton that's shared with the pool-detail page. The two step multipliers
 * below (1.6× per click) give the user roughly five clicks to go from
 * fit-to-data (`1`) to either extreme (`PRICE_ZOOM_MIN`/`PRICE_ZOOM_MAX`),
 * which empirically is enough to pull a WIDE-preset band into view without
 * letting the chart zoom into a meaningless flat line.
 */
const PRICE_ZOOM_STEP = 1.6
const PRICE_ZOOM_MIN = 0.1
const PRICE_ZOOM_MAX = 10

export const PriceHistoryComposite: FC<PriceHistoryCompositeProps> = observer(
  ({ createPositionStore, poolOhlcStore, height, className }) => {
    const pool = createPositionStore.pool
    const poolAddress = pool?.address.toViemAddress() ?? undefined
    const [zoomFactor, setZoomFactor] = useState(1)

    const handleZoomIn = useCallback(() => {
      setZoomFactor((prev) => Math.max(PRICE_ZOOM_MIN, prev / PRICE_ZOOM_STEP))
    }, [])
    const handleZoomOut = useCallback(() => {
      setZoomFactor((prev) => Math.min(PRICE_ZOOM_MAX, prev * PRICE_ZOOM_STEP))
    }, [])

    // Route/store fan-out: pushing `poolAddress` into the OHLC store is the
    // only side-effect this composite owns. `setActivePool` is idempotent and
    // no-ops when the address hasn't changed, so re-runs from sibling prop
    // changes are safe.
    useEffect(() => {
      poolOhlcStore.setActivePool(poolAddress)
      return () => {
        // Only clear if this composite actually activated the pool. Without
        // the guard, any sibling composite that mounts the OHLC store for
        // the same pool would see its data cleared out from under it on the
        // create page's unmount.
        if (poolOhlcStore.activePoolAddress === poolAddress?.toLowerCase()) {
          poolOhlcStore.setActivePool(undefined)
        }
      }
    }, [poolAddress, poolOhlcStore])

    if (!pool) return null

    const rawMin = parseFloatOrUndefined(createPositionStore.priceRangeCalculation.pricesAtTicks[0])
    const rawMax = parseFloatOrUndefined(createPositionStore.priceRangeCalculation.pricesAtTicks[1])
    const rawCurrent = createPositionStore.currentPrice

    // Reconcile orientation between the canonical price (token1 / token0,
    // produced by `tickToPriceString`) and the indexer's OHLC `close` field,
    // which is stored per-pool in whichever orientation the upstream Envio
    // mapping chose. We detect the mismatch empirically: if the current
    // price sits within two orders of magnitude of the median close we keep
    // everything as-is; otherwise we invert all three reference prices. This
    // is cheaper and more robust than threading a per-pool orientation flag
    // down from the subgraph — both orientations are reciprocals of each
    // other, so inverting is always the right fix when the log-distances
    // disagree.
    const orientation = detectOrientation({
      closeValues: poolOhlcStore.points
        .map((point) => point.close ?? point.open)
        .filter((value): value is number => value !== undefined && Number.isFinite(value)),
      referenceValue: rawCurrent,
    })

    const minPrice = orient(rawMin, orientation)
    const maxPrice = orient(rawMax, orientation)
    const currentPrice = orient(rawCurrent, orientation)

    // When we invert the bounds, their ORDER flips too — what was min
    // (lower) becomes larger than what was max. The view expects
    // `minPrice <= maxPrice` to draw the band correctly, so re-sort after
    // orientation normalisation.
    const [sortedMin, sortedMax] =
      minPrice !== undefined && maxPrice !== undefined && minPrice > maxPrice
        ? [maxPrice, minPrice]
        : [minPrice, maxPrice]

    return (
      <PriceHistoryWithRangeView
        points={poolOhlcStore.points}
        window={poolOhlcStore.window}
        minPrice={sortedMin}
        maxPrice={sortedMax}
        currentPrice={currentPrice}
        isInRange={createPositionStore.isProposedRangeInRange}
        isLoading={poolOhlcStore.isLoading}
        onWindowChange={(next) => poolOhlcStore.setWindow(next)}
        baseSymbol={pool.token0.symbol}
        quoteSymbol={pool.token1.symbol}
        height={height}
        className={className}
        zoomFactor={zoomFactor}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        canZoomIn={zoomFactor > PRICE_ZOOM_MIN}
        canZoomOut={zoomFactor < PRICE_ZOOM_MAX}
      />
    )
  },
)

PriceHistoryComposite.displayName = "PriceHistoryComposite"

export type PriceOrientation = "as-is" | "inverted"

/**
 * Decide whether the reference prices coming from `tickToPriceString` need
 * to be inverted to live in the same orientation as the indexer's OHLC
 * `close` series. Uses log-distance: if `referenceValue` sits closer to the
 * close median when inverted (e.g. 4000 vs close≈0.00025 → invert), flip.
 *
 * Exported for unit testing — no runtime consumer outside this file.
 */
export function detectOrientation({
  closeValues,
  referenceValue,
}: {
  closeValues: ReadonlyArray<number>
  referenceValue: number | undefined
}): PriceOrientation {
  if (
    referenceValue === undefined ||
    !Number.isFinite(referenceValue) ||
    referenceValue <= 0 ||
    closeValues.length === 0
  ) {
    return "as-is"
  }
  const median = closeMedian(closeValues)
  if (median === undefined || median <= 0) return "as-is"

  const distanceAsIs = Math.abs(Math.log10(referenceValue) - Math.log10(median))
  const distanceInverted = Math.abs(Math.log10(1 / referenceValue) - Math.log10(median))
  return distanceInverted < distanceAsIs ? "inverted" : "as-is"
}

function orient(value: number | undefined, orientation: PriceOrientation): number | undefined {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return value
  return orientation === "inverted" ? 1 / value : value
}

function closeMedian(values: ReadonlyArray<number>): number | undefined {
  if (values.length === 0) return undefined
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]
}

function parseFloatOrUndefined(value: string | undefined): number | undefined {
  if (value === undefined || value === "") return undefined
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : undefined
}
